import React from 'react';
import styled from 'astroturf/react';
import routes from 'js/core_pre_webpack/routes';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

const Container = styled.a`
  display: block;
  width: 100%;
  background: #0f373a;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  text-decoration: none;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    text-decoration: none;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  max-width: 697px;
  margin: 0 auto;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px 16px;
  }
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 317px;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

const Title = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 32px;
  color: #ffffff;
  margin: 0;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 28px;
  }
`;

const Description = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: #ffffff;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 18px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StyledButton = styled(Button)`
  background: #ffffff;
  border-color: #ffffff;
  color: #0f373a;
  height: 36px;
  padding: 0 16px;
  font-size: 14px;

  &:hover {
    background: #f5f5f5;
    border-color: #f5f5f5;
  }

  &:active {
    background: #e8e8e8;
    border-color: #e8e8e8;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

/**
 * Podcast Library Ad 2 component that promotes the podcast library feature.
 * Displays as a clickable banner in the home feed linking to the podcast library.
 * Features a dark teal background with white text and a call-to-action button.
 *
 * This component is automatically tracked with impression watching.
 */
export const PodcastLibraryAd2: React.FC = () => {
  const impressionSource = 'podcast_lib.home.ad-2';

  return (
    <ImpressionWatcher active={true} source={impressionSource}>
      <Container href={routes.podcast_library_url()}>
        <ContentWrapper>
          <TextSection>
            <TitleWrapper>
              <IconWrapper>
                <Icon name="books_movies_and_music" size={24} />
              </IconWrapper>
              <Title>Discover podcast library</Title>
            </TitleWrapper>
            <Description>
              Listen to paper insights in minutes, not hours
            </Description>
          </TextSection>
          <ButtonWrapper>
            <StyledButton size="sm" iconRight="arrow_forward">
              Browse podcast library
            </StyledButton>
          </ButtonWrapper>
        </ContentWrapper>
      </Container>
    </ImpressionWatcher>
  );
};
